import * as React from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useParams } from 'react-router';
import ImagePicker from '../components/ImagePicker';
import { RichTextEditor } from '../components/RichTextEditor';
import { sanitizeTelegramHtml } from '../utils';

const API_URL = import.meta.env.VITE_API_URL;
const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;

export default function ProductEdit() {
  const { id } = useParams();
  const productId = Number(id);
  const navigate = useNavigate();

  const [product, setProduct] = React.useState<any>({
    name: '',
    description: '',
    characteristic: '',
    compose: '',
    categoryId: '',
    subCategoryId: '',
    defaultPrice: '',
    defaultBiggerPrice: '',
    isVariable: false,
    productsCarouselId: null,
    imageUrl: null,
    isActive: true,
    promoPrice: '',
    isPromo: false,
    extraField_1: '',
    extraField_2: '',
    extraField_3: '',
  });
  const [categories, setCategories] = React.useState<any[]>([]);
  const [subCategories, setSubCategories] = React.useState<any[]>([]);
  const [prices, setPrices] = React.useState<any[]>([]);
  const [enableVariations, setEnableVariations] = React.useState(false);
  const [image, setImage] = React.useState<File | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);
  const [carousels, setCarousels] = React.useState<any[]>([]);
  const [productsCarouselId, setProductsCarouselId] = React.useState<any>(null);

  React.useEffect(() => {
    fetch(`${API_URL}/product?productId=${productId}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.product) return;
        setProduct({
          ...data.product,
          isVariable: data.product.isVariable ?? false,
          extraField_1: data.product.extraField_1 ?? '',
          extraField_2: data.product.extraField_2 ?? '',
          extraField_3: data.product.extraField_3 ?? '',
        });
        setPrices(
          Array.isArray(data.prices) && data.prices.length > 0
            ? data.prices
            : [],
        );
        setEnableVariations(data.product.isVariable || data.prices?.length > 0);
        if (data.product.imageUrl)
          setImageUrl(`${IMAGE_URL}/${data.product.imageUrl}`);
        if (data.product.productsCarouselId)
          setProductsCarouselId(data.product.productsCarouselId);
        if (data.product.mediaId) setMediaId(data.product.mediaId);
      });
    fetch(`${API_URL}/categories`)
      .then((r) => r.json())
      .then(setCategories);
    fetch(`${API_URL}/sub-categories`)
      .then((r) => r.json())
      .then((r) => setSubCategories(r.data));
    fetch(`${API_URL}/carousel?perPage=1000`)
      .then((r) => r.json())
      .then((r) => setCarousels(r.data));
  }, [productId]);

  const filteredSubCategories = subCategories.filter(
    (s) => s.categoryId === product.categoryId,
  );

  const updatePrice = (index: number, field: string, value: any) => {
    const next = [...prices];
    next[index] = { ...next[index], [field]: value };
    setPrices(next);
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append('name', product.name);
    formData.append('description', sanitizeTelegramHtml(product.description));
    formData.append(
      'characteristic',
      sanitizeTelegramHtml(product.characteristic),
    );
    formData.append('compose', sanitizeTelegramHtml(product.compose));
    formData.append('categoryId', product.categoryId ?? '');
    formData.append('subCategoryId', product.subCategoryId ?? '');
    formData.append('isActive', product.isActive ? 'true' : 'false');
    formData.append('isPromo', product.isPromo ? 'true' : 'false');
    formData.append('extraField_1', product.extraField_1 ?? '');
    formData.append('extraField_2', product.extraField_2 ?? '');
    formData.append('extraField_3', product.extraField_3 ?? '');

    if (!enableVariations) {
      formData.append('defaultPrice', product.defaultPrice ?? '');
      formData.append('defaultBiggerPrice', product.defaultBiggerPrice ?? '');
      if (product.isPromo) {
        formData.append('promoPrice', product.promoPrice ?? '0');
      }
    }

    if (enableVariations) {
      const normalizedPrices = prices.map((p) => ({
        ...p,
        promoPrice: product.isPromo ? p.promoPrice : null,
      }));
      formData.append('prices', JSON.stringify(normalizedPrices));
    }

    if (productsCarouselId)
      formData.append('productsCarouselId', productsCarouselId);
    if (image) formData.append('image', image);
    else if (mediaId) formData.append('mediaId', String(mediaId));

    await fetch(`${API_URL}/product/${productId}`, {
      method: 'PUT',
      body: formData,
    });
    navigate('/products');
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Box sx={{ mt: 1, mb: 1, fontSize: 16, fontWeight: 'bold' }}>
        {product.name}
      </Box>

      {!product.isActive && (
        <Box sx={{ mb: 1, fontSize: 14, fontStyle: 'italic' }}>
          Продукт не активний
        </Box>
      )}

      <TextField
        fullWidth
        label="Назва"
        value={product.name}
        onChange={(e) => setProduct({ ...product, name: e.target.value })}
        margin="normal"
        sx={{ mt: 2, mb: 2 }}
      />

      <RichTextEditor
        value={product.description}
        onChange={(html) => setProduct({ ...product, description: html })}
        label="Опис"
        minRows={4}
        sx={{ mb: 2 }}
      />

      <RichTextEditor
        value={product.characteristic}
        onChange={(html) => setProduct({ ...product, characteristic: html })}
        label="Характеристики"
        minRows={4}
        sx={{ mb: 2 }}
      />

      <RichTextEditor
        value={product.compose}
        onChange={(html) => setProduct({ ...product, compose: html })}
        label="Склад"
        minRows={4}
        sx={{ mb: 2 }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={product.isActive}
            onChange={(e) =>
              setProduct({ ...product, isActive: e.target.checked })
            }
          />
        }
        label="Відобразити продукт у боті"
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Категорія</InputLabel>
        <Select
          value={product.categoryId}
          onChange={(e) =>
            setProduct({
              ...product,
              categoryId: e.target.value,
              subCategoryId: '',
            })
          }
        >
          {categories.map((c) => (
            <MenuItem key={c.categoryId} value={c.categoryId}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {filteredSubCategories.length > 0 && (
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Підкатегорія</InputLabel>
          <Select
            value={product.subCategoryId}
            onChange={(e) =>
              setProduct({ ...product, subCategoryId: e.target.value })
            }
          >
            {filteredSubCategories.map((s) => (
              <MenuItem key={s.subCategoryId} value={s.subCategoryId}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!product.isVariable && (
        <FormControlLabel
          control={
            <Checkbox
              checked={enableVariations}
              onChange={(e) => {
                const checked = e.target.checked;
                setEnableVariations(checked);
                if (checked && prices.length === 0) {
                  setPrices([
                    { weight: '', price: '', biggerPrice: '', promoPrice: '' },
                  ]);
                }
              }}
            />
          }
          label="Використовувати ціни варіації"
        />
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={product.isPromo}
            onChange={(e) =>
              setProduct({ ...product, isPromo: e.target.checked })
            }
          />
        }
        label="Це акційний товар"
      />

      {!enableVariations && (
        <Box sx={{ mb: 3 }}>
          <TextField
            type="number"
            fullWidth
            label="Ціна"
            value={product.defaultPrice ?? ''}
            onChange={(e) =>
              setProduct({
                ...product,
                defaultPrice: e.target.value === '' ? '' : +e.target.value,
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            type="number"
            fullWidth
            label="Підвищена ціна"
            value={product.defaultBiggerPrice ?? ''}
            onChange={(e) =>
              setProduct({
                ...product,
                defaultBiggerPrice:
                  e.target.value === '' ? '' : +e.target.value,
              })
            }
          />
        </Box>
      )}

      {enableVariations &&
        prices.map((p, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}
          >
            <TextField
              label="Вага"
              value={p.weight}
              onChange={(e) => updatePrice(i, 'weight', e.target.value)}
            />
            <TextField
              type="number"
              label="Ціна"
              value={p.price ?? ''}
              onChange={(e) =>
                updatePrice(
                  i,
                  'price',
                  e.target.value === '' ? '' : +e.target.value,
                )
              }
              inputProps={{ min: 1 }}
            />
            <TextField
              type="number"
              label="Підвищена ціна"
              value={p.biggerPrice ?? ''}
              onChange={(e) =>
                updatePrice(
                  i,
                  'biggerPrice',
                  e.target.value === '' ? '' : +e.target.value,
                )
              }
              inputProps={{ min: 1 }}
            />
            {product.isPromo && (
              <TextField
                type="number"
                label="Акційна ціна"
                value={p.promoPrice ?? ''}
                onChange={(e) =>
                  updatePrice(
                    i,
                    'promoPrice',
                    e.target.value === '' ? '' : +e.target.value,
                  )
                }
                inputProps={{ min: 1 }}
              />
            )}
            <IconButton
              onClick={() =>
                setPrices(prices.filter((_, index) => index !== i))
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}

      {enableVariations && (
        <Button
          variant="outlined"
          onClick={() =>
            setPrices([
              ...prices,
              { weight: '', price: '', biggerPrice: '', promoPrice: '' },
            ])
          }
        >
          Додати варіацію
        </Button>
      )}

      {!enableVariations && product.isPromo && (
        <TextField
          type="number"
          fullWidth
          label="Акційна ціна"
          value={product.promoPrice ?? ''}
          onChange={(e) =>
            setProduct({
              ...product,
              promoPrice: e.target.value === '' ? '' : +e.target.value,
            })
          }
          sx={{ mb: 3 }}
        />
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Каталог</InputLabel>
        <Select
          style={{ height: 80 }}
          value={productsCarouselId}
          onChange={(e) => setProductsCarouselId(e.target.value)}
        >
          {carousels.map((c) => (
            <MenuItem key={c.productsCarouselId} value={c.productsCarouselId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <img src={`${IMAGE_URL}/${c.image}`} width={48} height={48} />
                <Typography>#{c.productsCarouselId}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {productsCarouselId && (
          <Box sx={{ mt: 1 }}>
            <a
              href={`/carousel/${productsCarouselId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'underline', color: '#1976d2' }}
            >
              Переглянути каталог №{productsCarouselId}
            </a>
          </Box>
        )}
      </FormControl>

      <ImagePicker
        title="Зображення продукту"
        value={imageUrl || ''}
        recommendedSizeLabel={512}
        onChange={(value) => {
          if (value.type === 'file') {
            setImage(value.file);
            setMediaId(null);
            setImageUrl(URL.createObjectURL(value.file));
          }
          if (value.type === 'media') {
            setImage(null);
            setMediaId(value.mediaId);
            setImageUrl(value.url);
          }
        }}
      />

      <Accordion sx={{ mt: 3, mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Редагування додаткових полів</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <RichTextEditor
              value={product.extraField_1}
              onChange={(html) =>
                setProduct({ ...product, extraField_1: html })
              }
              label="Додаткове поле №1"
              minRows={3}
            />
            <RichTextEditor
              value={product.extraField_2}
              onChange={(html) =>
                setProduct({ ...product, extraField_2: html })
              }
              label="Додаткове поле №2"
              minRows={3}
            />
            <RichTextEditor
              value={product.extraField_3}
              onChange={(html) =>
                setProduct({ ...product, extraField_3: html })
              }
              label="Додаткове поле №3"
              minRows={3}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Button variant="contained" onClick={handleSave}>
        Зберегти
      </Button>
    </Box>
  );
}
